import { useState } from "react";
import { Box, Button, FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SideDrawer, { FieldLabel, fieldSx, primaryButtonSx } from "components/SideDrawer";
import { useToast } from "components/Toast";
import { EsimOrder, requestEsimRefund, resolveEsimRefund } from "services/esim";
import { getApiError } from "services/finance";

// Only TRIP_CANCELLATION is confirmed by the backend so far; "OTHER" lets the
// admin type the reason code until the full enum is known.
const REFUND_REASONS = [{ value: "TRIP_CANCELLATION", label: "Trip cancellation" }];

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  order: EsimOrder;
}

const useRefresh = (orderRef: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["esimOrder", orderRef] });
    queryClient.invalidateQueries({ queryKey: ["esimOrders"] });
  };
};

export const RequestRefundDrawer = ({ open, onClose, order }: DrawerProps) => {
  const { showToast } = useToast();
  const refresh = useRefresh(order.order_ref);
  const [reason, setReason] = useState(REFUND_REASONS[0].value);
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");

  const finalReason = reason === "OTHER" ? customReason.trim() : reason;

  const mutation = useMutation({
    mutationFn: () => requestEsimRefund(order.order_ref, { reason: finalReason, notes }),
    onSuccess: (res) => {
      showToast(res?.message || "Refund requested from Airalo", "success");
      refresh();
      onClose();
    },
    onError: (error) => showToast(getApiError(error, "Failed to request refund"), "error"),
  });

  return (
    <SideDrawer open={open} onClose={onClose} title="Request refund">
      <Box display="flex" flexDirection="column" gap="16px">
        <Box>
          <FieldLabel>Reason</FieldLabel>
          <TextField
            select
            fullWidth
            sx={fieldSx}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            {REFUND_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
            <MenuItem value="OTHER">Other (enter code)</MenuItem>
          </TextField>
        </Box>
        {reason === "OTHER" && (
          <Box>
            <FieldLabel>Reason code</FieldLabel>
            <TextField
              fullWidth
              sx={fieldSx}
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value.toUpperCase())}
              placeholder="e.g. TRIP_CANCELLATION"
            />
          </Box>
        )}
        <Box>
          <FieldLabel>Notes</FieldLabel>
          <TextField
            fullWidth
            multiline
            rows={3}
            sx={fieldSx}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
        <Button
          variant="contained"
          sx={primaryButtonSx}
          disabled={!finalReason || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Requesting..." : "Request refund"}
        </Button>
      </Box>
    </SideDrawer>
  );
};

export const ResolveRefundDrawer = ({ open, onClose, order }: DrawerProps) => {
  const { showToast } = useToast();
  const refresh = useRefresh(order.order_ref);
  const [customerRefund, setCustomerRefund] = useState(String(order.total_price ?? ""));
  const [approved, setApproved] = useState(true);
  const [creditUsd, setCreditUsd] = useState("");
  const [notes, setNotes] = useState("");

  const refundValid = customerRefund !== "" && Number(customerRefund) >= 0;
  const creditValid = !approved || (creditUsd !== "" && Number(creditUsd) >= 0);

  const mutation = useMutation({
    mutationFn: () =>
      resolveEsimRefund(order.order_ref, {
        customer_refund_ngn: Number(customerRefund),
        airalo_approved: approved,
        airalo_credit_usd: approved ? Number(creditUsd) : 0,
        notes,
      }),
    onSuccess: (res) => {
      showToast(res?.message || "Refund recorded", "success");
      refresh();
      onClose();
    },
    onError: (error) => showToast(getApiError(error, "Failed to record refund"), "error"),
  });

  return (
    <SideDrawer open={open} onClose={onClose} title="Resolve refund">
      <Box display="flex" flexDirection="column" gap="16px">
        <Box>
          <FieldLabel>Refund to customer (₦)</FieldLabel>
          <TextField
            fullWidth
            type="number"
            sx={fieldSx}
            value={customerRefund}
            onChange={(e) => setCustomerRefund(e.target.value)}
            error={!refundValid}
            helperText={!refundValid ? "Enter 0 or more" : "Defaults to the order total"}
          />
        </Box>
        <FormControlLabel
          control={<Switch checked={approved} onChange={(e) => setApproved(e.target.checked)} />}
          label="Airalo approved the refund"
        />
        {approved && (
          <Box>
            <FieldLabel>Airalo credit (USD)</FieldLabel>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: "any" }}
              sx={fieldSx}
              value={creditUsd}
              onChange={(e) => setCreditUsd(e.target.value)}
              error={creditUsd !== "" && !creditValid}
            />
          </Box>
        )}
        <Box>
          <FieldLabel>Notes</FieldLabel>
          <TextField
            fullWidth
            multiline
            rows={3}
            sx={fieldSx}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
        <Button
          variant="contained"
          sx={primaryButtonSx}
          disabled={!refundValid || !creditValid || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Saving..." : "Record outcome"}
        </Button>
      </Box>
    </SideDrawer>
  );
};
